import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Paper, { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { RefundSaleOrderImpactAlert } from 'features/transaction-details/components/RefundSaleOrderImpactAlert';
import {
	Alert,
	AlertColor,
	Backdrop,
	Box,
	CircularProgress,
	FormControl,
	FormHelperText,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Snackbar,
	TextField,
	Toolbar,
	Typography,
} from '@mui/material';
import { checkShowErrorMessage } from '../../../common/helpers';
import { extractErrorMessage } from 'features/quotation/helpers';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@mui/icons-material/Close';
import { IAuthorizationAllowedToRefund } from 'features/transactions/types';
import { getAuthorizationAllowedToRefundByTransactionId } from '../../services';
import { refundAuthorization } from '../../../authorization/services';
import { IRefundAuthorization, IRefundAuthorizationForm } from '../../../authorization/types';

function PaperComponent(props: PaperProps) {
	return (
		<Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
			<Paper {...props} />
		</Draggable>
	);
}

const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.modal + 1,
		color: '#fff',
	},
	helperTextDanger: {
		color: 'red',
	},
}));

interface ShowAlertState {
	show: boolean;
	severity: AlertColor;
	message: string;
}

export interface RefundLimitsFromDetail {
	maxRefundable: number;
	activeCrossed: number;
	authorizationCode: string;
}

export interface CreateRefundModalProps {
	transactionId: number;
	authorizationId?: number;
	coinCode?: string;
	refundLimits?: RefundLimitsFromDetail;
	dealId?: number;
	saleOrderId?: string | null;
	onNavigationError?: (message: string) => void;
	open: boolean;
	handleClose: () => void;
	reloadList: () => void;
}

export function CreateRefundAuthorizationModal({
	transactionId,
	authorizationId,
	coinCode,
	refundLimits,
	dealId,
	saleOrderId = null,
	onNavigationError,
	open,
	handleClose,
	reloadList,
}: CreateRefundModalProps): JSX.Element {
	const classes = useStyles();
	const displayCoinCode = coinCode ?? 'USD';

	const {
		control,
		handleSubmit,
		formState: { errors, isValid },
		reset,
		setValue,
		trigger,
	} = useForm<IRefundAuthorizationForm>({
		mode: 'all',
		defaultValues: {
			authorizationId: authorizationId || 0,
			amountToRefund: 0,
		},
	});

	const [loading, setLoading] = useState<boolean>(false);
	const [maxRefund, setMaxRefund] = useState<number>(0);
	const amountToRefund = useWatch({ control, name: 'amountToRefund' });
	const displayRefundAmount = typeof amountToRefund === 'number' && amountToRefund > 0 ? amountToRefund : maxRefund;
	const [authorizationsAllowedToRefund, setAuthorizationsAllowedToRefund] = useState<IAuthorizationAllowedToRefund[]>(
		[],
	);
	const [authorizationsLoaded, setAuthorizationsLoaded] = useState(false);
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);

	const applyFullRefundAmount = useCallback((): void => {
		if (maxRefund > 0) {
			setValue('amountToRefund', maxRefund, { shouldValidate: false, shouldDirty: true });
			void trigger('amountToRefund');
		}
	}, [maxRefund, setValue, trigger]);

	const resolveMaxRefund = useCallback(
		(apiMax: number): number => {
			if (refundLimits) {
				return Math.min(apiMax, refundLimits.maxRefundable);
			}
			return apiMax;
		},
		[refundLimits],
	);

	const syncMaxRefundForAuthorization = useCallback(
		(authId: number): void => {
			const maybeElement = authorizationsAllowedToRefund.find((auth) => auth.id === authId);
			const apiMax = maybeElement?.maxAllowedToRefund ?? 0;
			setMaxRefund(resolveMaxRefund(apiMax));
		},
		[authorizationsAllowedToRefund, resolveMaxRefund],
	);

	const getAuthorizationAllowedToRefund = async (): Promise<void> => {
		try {
			setLoading(true);
			const authorizationList = (await getAuthorizationAllowedToRefundByTransactionId(transactionId)).data;
			setAuthorizationsAllowedToRefund(authorizationList);
			setAuthorizationsLoaded(true);
			setLoading(false);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(
					error,
					'Ocurrió un error al recibir el listado de autorizaciones permitidas para devolución',
				),
			});
			setLoading(false);
		}
	};

	useEffect(() => {
		if (open && !authorizationsLoaded) {
			getAuthorizationAllowedToRefund();
		}
		if (!open && authorizationsLoaded) {
			setAuthorizationsLoaded(false);
		}
	}, [open, authorizationsLoaded]);

	useEffect(() => {
		if (!open) {
			return;
		}
		reset({
			authorizationId: authorizationId || 0,
			amountToRefund: 0,
		});
		setMaxRefund(refundLimits?.maxRefundable ?? 0);
	}, [open, authorizationId, refundLimits?.maxRefundable, reset]);

	useEffect(() => {
		if (!open || authorizationsAllowedToRefund.length === 0) {
			return;
		}

		const selectedAuthId = authorizationId && authorizationId > 0 ? authorizationId : 0;
		if (selectedAuthId > 0) {
			syncMaxRefundForAuthorization(selectedAuthId);
		}
	}, [open, authorizationId, authorizationsAllowedToRefund, syncMaxRefundForAuthorization]);

	const onSubmit = async (data: IRefundAuthorizationForm): Promise<void> => {
		try {
			setLoading(true);
			await refundAuthorization(data.authorizationId, {
				amountToRefund: data.amountToRefund,
			} as IRefundAuthorization);
			setAlert({
				show: true,
				severity: 'success',
				message: 'Devolución realizada satisfactoriamente',
			});
			reloadList();
			setLoading(false);
			setMaxRefund(0);
			reset();
			setAuthorizationsLoaded(false);
			handleClose();
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al realizar devolución'),
			});
			setLoading(false);
		}
	};

	const handleSelectAuthorization = (
		event: SelectChangeEvent<number>,
		onChange: (...event: unknown[]) => void,
	): void => {
		const selectedId = Number(event.target.value);
		syncMaxRefundForAuthorization(selectedId);
		setValue('amountToRefund', 0, { shouldValidate: false, shouldDirty: false });
		onChange(selectedId);
	};

	const onClose = (): void => {
		reset();
		setMaxRefund(0);
		setAuthorizationsLoaded(false);
		handleClose();
	};

	return (
		<div>
			<Backdrop className={classes.backdrop} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Dialog open={open} PaperComponent={PaperComponent} aria-labelledby="draggable-dialog-title">
				<Toolbar
					id="draggable-dialog-title"
					style={{ cursor: 'move' }}
					sx={{ display: 'flex', justifyContent: 'space-between' }}
				>
					<Typography variant="h5" color="primary">
						Realizar Devolución
					</Typography>
					<IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
						<CloseIcon />
					</IconButton>
				</Toolbar>
				<DialogContent>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Grid container gap={2}>
							{refundLimits && refundLimits.activeCrossed > 0 && (
								<Grid item xs={12}>
									<Alert severity="info">
										Hay {refundLimits.activeCrossed} {displayCoinCode} cruzados en e-tickets. Solo podés devolver hasta{' '}
										{refundLimits.maxRefundable} {displayCoinCode} (monto sin cruzar). Para devolver más, desvinculá los
										boletos primero.
									</Alert>
								</Grid>
							)}
							{dealId != null && dealId > 0 && (
								<Grid item xs={12}>
									<RefundSaleOrderImpactAlert
										dealId={dealId}
										saleOrderId={saleOrderId}
										amount={displayRefundAmount}
										coinCode={displayCoinCode}
										onNavigationError={onNavigationError}
									/>
								</Grid>
							)}
							<Grid item xs={12}>
								<Controller
									name="authorizationId"
									control={control}
									rules={{
										required: {
											value: true,
											message: 'Por favor seleccione el código de la autorización',
										},
									}}
									render={({ field: { value, onChange }, fieldState: { invalid, error } }) => (
										<FormControl color={invalid ? 'error' : undefined} sx={{ width: '100%', height: '100%' }}>
											<InputLabel id="authorization-select-label">Código de autorización</InputLabel>
											<Select
												labelId="authorization-select-label"
												id="e-ticket-select"
												label="Código de autorización"
												value={value}
												disabled={
													loading ||
													authorizationsAllowedToRefund.length === 0 ||
													(authorizationId != null && authorizationId > 0)
												}
												onChange={(event) => handleSelectAuthorization(event, onChange)}
												error={Boolean(errors.authorizationId)}
											>
												{authorizationsAllowedToRefund.length > 0 &&
													authorizationsAllowedToRefund.map((authorization) => (
														<MenuItem key={authorization.id} value={authorization.id}>
															{authorization.authorizationCode}
														</MenuItem>
													))}
											</Select>
											{error && <FormHelperText sx={{ color: 'red' }}>{error.message}</FormHelperText>}
											{!loading && authorizationsAllowedToRefund.length === 0 && (
												<FormHelperText sx={{ color: 'red' }}>
													NO existen códigos de autorización para realizar devoluciones
												</FormHelperText>
											)}
										</FormControl>
									)}
								/>
							</Grid>

							{maxRefund > 0 && (
								<Grid item xs={12}>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
										<Button
											variant="outlined"
											color="warning"
											fullWidth
											disabled={loading}
											onClick={applyFullRefundAmount}
											type="button"
										>
											Devolver total · {maxRefund} {displayCoinCode}
										</Button>
										<Typography variant="body2" color="text.secondary">
											O ingresá un monto parcial:
										</Typography>
									</Box>
								</Grid>
							)}

							<Grid item xs={12}>
								<Controller
									name="amountToRefund"
									control={control}
									rules={{
										required: { value: true, message: 'Este campo es requerido' },
										min: {
											value: 1,
											message: 'El monto debe ser mayor que 0',
										},
										max: {
											value: maxRefund,
											message: `La autorización no permite una devolución de más de ${maxRefund} ${displayCoinCode}`,
										},
										pattern: {
											value: /^(0|[1-9]\d*)(\.\d+)?$/,
											message: 'El valor debe ser numérico',
										},
									}}
									render={({ field }) => (
										<TextField
											variant="outlined"
											required
											fullWidth
											label="Monto"
											type="number"
											disabled={loading || authorizationsAllowedToRefund.length === 0}
											helperText={checkShowErrorMessage(Boolean(errors.amountToRefund), errors.amountToRefund?.message)}
											error={Boolean(errors.amountToRefund)}
											{...field}
											onChange={(event) => field.onChange(Number(event.target.value))}
										/>
									)}
								/>
								{maxRefund > 0 && (
									<Typography variant="caption" sx={{ color: 'success.main', display: 'block', mt: 0.5 }}>
										Máximo disponible: {maxRefund} {displayCoinCode}
									</Typography>
								)}
							</Grid>

							<Grid item xs={12} display={'flex'} gap={'10px'} justifyContent="flex-end">
								<Button variant="contained" disabled={loading || !isValid} color="warning" fullWidth type="submit">
									Confirmar Devolución
								</Button>
							</Grid>
						</Grid>
					</form>
				</DialogContent>
			</Dialog>
			<Snackbar open={alert.show} autoHideDuration={1500} onClose={() => setAlert(defaultAlertState)}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</div>
	);
}
